import React from 'react';
import { Activity } from '../../types/Activity';

interface ActivityCardProps {
  activity: Activity & { day: number };
}

const ActivityCard: React.FC<ActivityCardProps> = ({ activity }) => {
  const getActivityIcon = (category?: string) => {
    if (!category) return '🎟️';
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('sightseeing') || categoryLower.includes('tour')) return '🏛️';
    if (categoryLower.includes('adventure') || categoryLower.includes('sports')) return '🏃';
    if (categoryLower.includes('cultural') || categoryLower.includes('museum')) return '🎭';
    if (categoryLower.includes('nature') || categoryLower.includes('park')) return '🌳';
    if (categoryLower.includes('water') || categoryLower.includes('beach')) return '🏖️';
    if (categoryLower.includes('food') || categoryLower.includes('dining')) return '🍽️';
    if (categoryLower.includes('shopping')) return '🛍️';
    if (categoryLower.includes('entertainment') || categoryLower.includes('show')) return '🎪';
    if (categoryLower.includes('spa') || categoryLower.includes('wellness')) return '🧘';
    return '🎟️';
  };

  const getModeIcon = (mode: string) => {
    if (mode === 'private') return '👤';
    if (mode === 'only_ticket') return '🎫';
    return '👥';
  };

  const getModeColor = (mode: string) => {
    if (mode === 'private') return '#e74c3c';
    if (mode === 'only_ticket') return '#9b59b6';
    return '#3498db';
  };

  const getVariantInfo = (activity: Activity) => {
    if (!activity.variants || activity.variants.length === 0) {
      return { mode: 'shared', price: activity.from_price_paise };
    }
    
    const activeVariants = activity.variants.filter(v => v.is_active);
    if (activeVariants.length === 0) {
      return { mode: 'shared', price: activity.from_price_paise };
    }
    
    // Get the first active variant for display
    const firstVariant = activeVariants[0];
    return { 
      mode: firstVariant.mode, 
      price: firstVariant.price_paise,
      variantCount: activity.variant_count || activity.variants.length
    };
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return 'TBD';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };


  return (
    <div style={{
      border: '1px solid #e1e5e9',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      gap: '16px'
    }}>
      {/* Activity Image */}
      <div style={{ flex: '0 0 100px' }}>
        {activity.picture_url ? (
          <div style={{ position: 'relative' }}>
            <img
              src={activity.picture_url}
              alt={activity.name}
              style={{
                width: '100%',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '8px'
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.setAttribute('style', 'display: flex');
              }}
            />
            {/* Fallback activity icon */}
            <div style={{
              width: '100%',
              height: '80px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: '#6c757d',
              position: 'absolute',
              top: 0,
              left: 0
            }}>
              {getActivityIcon(activity.category)}
            </div>
          </div>
        ) : (
          <div style={{
            width: '100%',
            height: '80px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: '#6c757d'
          }}>
            {getActivityIcon(activity.category)}
          </div>
        )}
      </div>

      {/* Activity Details */}
      <div style={{ flex: 1 }}>
        {/* Header */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
              {activity.name}
            </h4>
            {(() => {
              const variantInfo = getVariantInfo(activity);
              return (
                <span style={{
                  fontSize: '11px',
                  backgroundColor: getModeColor(variantInfo.mode),
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>{getModeIcon(variantInfo.mode)}</span>
                  <span>{variantInfo.mode === 'only_ticket' ? 'Only Ticket' : variantInfo.mode}</span>
                  {variantInfo.variantCount && variantInfo.variantCount > 1 && (
                    <span>({variantInfo.variantCount} variants)</span>
                  )}
                </span>
              );
            })()}
          </div>
          {activity.description && (
            <p style={{ 
              margin: 0, 
              fontSize: '12px', 
              color: '#6c757d',
              lineHeight: '1.4'
            }}>
              {activity.description}
            </p>
          )}
        </div>

        {/* Activity Info Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>Duration</div>
            <div style={{ fontSize: '13px', fontWeight: '500' }}>
              {formatDuration(activity.duration_minutes)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>Start Time</div>
            <div style={{ fontSize: '13px', fontWeight: '500' }}>
              {activity.start_time || 'TBD'}
            </div>
          </div>
        </div>

        {/* Location and Category */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px' }}>📍</span>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              {activity.city || 'Location TBD'}
            </span>
          </div>
          {activity.category && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '12px' }}>{getActivityIcon(activity.category)}</span>
              <span style={{ fontSize: '11px', color: '#6c757d' }}>
                {activity.category}
              </span>
            </div>
          )}
        </div>

        {/* Remarks */}
        {activity.remarks && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '4px' }}>Remarks</div>
            <div style={{ 
              fontSize: '11px', 
              color: '#495057',
              fontStyle: 'italic',
              padding: '6px 8px',
              backgroundColor: '#fff3cd',
              borderRadius: '6px',
              border: '1px solid #ffeaa7'
            }}>
              {activity.remarks}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityCard;
